import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { IconButton } from "@/components/IconButton";

import styles from "./OutlineDraftScreen.module.css";

/**
 * „?“ beside „Übernehmen“ (UX review 26.10.2026, B-6): the drawing's gestures
 * and what „Übernehmen“ does, shut until asked. Above the stage they cost the
 * surface four lines of the first screen; under „?“ they are one press away
 * and the surface begins where the work does.
 *
 * Not a dialog: nothing in it is a control, the page stays in reach. It
 * opens under the button and goes with the button again, with Escape — the
 * focus back on the button where it had gone inside — or with a press
 * anywhere outside it. Its words stay in the markup while it is shut
 * (`hidden`), so the button can name what it opens (`aria-controls`).
 */
export function OutlineHelp({ children }: { children: ReactNode }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (event.target instanceof Node && wrap.current?.contains(event.target)) return;
      setOpen(false);
    };
    // In the capture phase on the document, as the button's tooltip listens:
    // one Escape closes both, and nothing behind the help — the board's own
    // Escape that lets go of a wall or of the maximized view — hears it.
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      if (wrap.current?.contains(document.activeElement)) button.current?.focus();
      setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", escape, true);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", escape, true);
    };
  }, [open]);

  return (
    <div ref={wrap} className={styles.help}>
      <IconButton
        ref={button}
        label="Hilfe zum Umriss"
        icon={
          <span className={styles.helpMark} aria-hidden="true">
            ?
          </span>
        }
        className={styles.helpButton}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((shown) => !shown)}
      />
      <div id={id} className={styles.helpPanel} hidden={!open}>
        {children}
      </div>
    </div>
  );
}
